import React from "react";

import { Eyebrow, Title, ShieldIcon } from "../components/CommonComponents";
import MetaInfo from "../components/MetaInfo";
import Header from "../components/Header";
import Footer from "../components/Footer";

const hostelSections = [
    {
        number: "01",
        id: "respect",
        title: "Respect for Others",
        label: "Community",
        description: "A respectful residential community begins with consideration for every person who lives, works, or visits the hostel.",
        points: [
            "Treat all residents, staff, and visitors with respect, kindness, and courtesy.",
            "Avoid engaging in behavior that may cause harm, discomfort, or distress to others.",
            "Respect the privacy and personal space of fellow residents.",
        ],
    },
    {
        number: "02",
        id: "quiet-hours",
        title: "Quiet Hours",
        label: "Study & Rest",
        description: "Maintaining appropriate noise levels helps create an environment suitable for both academic work and rest.",
        points: [
            "Quiet hours are enforced from 9:30 PM to 7:00 AM every day.",
            "Keep noise levels to a minimum during quiet hours to ensure a healthy environment for studying and rest.",
        ],
    },
    {
        number: "03",
        id: "cleanliness",
        title: "Cleanliness",
        label: "Living Environment",
        description: "Every boarder shares responsibility for maintaining a clean, orderly, and well-managed residential environment.",
        points: [
            "Keep your living space clean and tidy at all times.",
            "Dispose of garbage properly and follow recycling guidelines.",
            "Report any maintenance issues promptly to the hostel admin or warden.",
        ],
    },
    {
        number: "04",
        id: "safety",
        title: "Safety and Security",
        label: "Protection",
        description: "Responsible security practices are essential for protecting residents, staff, property, and the hostel community.",
        points: [
            "Do not compromise the safety and security of yourself or others.",
            "Always lock your room when leaving and do not prop doors open.",
            "Do not allow unauthorized individuals to enter the hostel premises.",
        ],
    },
    {
        number: "05",
        id: "substances",
        title: "Use of Alcohol and Toxic Substances",
        label: "Prohibited",
        description: "The hostel maintains strict standards regarding substances that may compromise safety, discipline, and student welfare.",
        points: [
            "The consumption of alcohol and use of any illegal toxic substances are strictly prohibited in the hostel.",
            "Do not bring or store alcohol or any illegal toxic substances on hostel premises.",
        ],
    },
    {
        number: "06",
        id: "smoking",
        title: "Smoking",
        label: "Prohibited",
        description: "Hostel buildings and individual residential rooms are maintained as smoke-free spaces.",
        points: [
            "Smoking is strictly prohibited within the hostel building, including individual rooms.",
        ],
    },
    {
        number: "07",
        id: "university-policies",
        title: "Compliance with University Policies",
        label: "Responsibility",
        description: "Hostel residents remain members of the wider University community and are expected to follow institutional standards.",
        points: [
            "Adhere to all university policies and regulations, including those related to conduct, academic integrity, and proper use of facilities.",
        ],
    },
    {
        number: "08",
        id: "disciplinary-action",
        title: "Disciplinary Action",
        label: "Accountability",
        description: "Non-compliance with hostel standards may lead to action proportionate to the nature and seriousness of the violation.",
        points: [
            "Violation of the hostel code of conduct will result in disciplinary action, including warnings, fines, suspension of hostel privileges, or expulsion from the hostel.",
            "Serious or repeated violations may also result in disciplinary action by the university.",
        ],
    },
];

export default function CodeOfConductHostel() {
    return (
        <>
            <MetaInfo param="" id="about" />
            <Header school="common" />

            <main className="bg-white text-[#132337]">
                {/* HERO */}
                <section className="relative overflow-hidden bg-[#071A2F] pt-[100px] text-white">

                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute inset-y-0 right-0 w-[43%] bg-[#0C2744]" />
                        <div className="absolute right-0 top-0 h-full w-[43%] opacity-[0.045] [background-image:linear-gradient(rgba(255,255,255,.7)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.7)_1px,transparent_1px)] [background-size:52px_52px]" />
                        <div className="absolute -left-[250px] bottom-[-350px] h-[620px] w-[620px] rounded-full border border-white/[0.05]" />
                        <div className="absolute -left-[170px] bottom-[-270px] h-[460px] w-[460px] rounded-full border border-[#D5AD57]/10" />
                        <div className="absolute bottom-0 left-0 h-[5px] w-[30%] bg-[#8B1E3F]" />
                        <div className="absolute bottom-0 left-[30%] h-[5px] w-[15%] bg-[#D5AD57]" />
                    </div>

                    <div className="relative mx-auto grid min-h-[590px] w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)] lg:grid-cols-[1.08fr_.92fr]">

                        {/* LEFT */}
                        <div className="flex flex-col justify-center py-16 pr-8 lg:py-20 lg:pr-20">

                            <div className="flex items-center gap-4 text-sm font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                <span className="h-px w-12 bg-current" />
                                Hostel Board Code of Conduct
                            </div>

                            <h1 className="mt-7 max-w-[800px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(52px,6vw,88px)] font-normal leading-[0.96] tracking-[-0.045em]">
                                Living together
                                <span className="block text-[#D5AD57]">
                                    with respect & responsibility.
                                </span>
                            </h1>

                            <p className="mt-8 max-w-[680px] text-[17px] leading-8 text-white/65">
                                The boarders of the hostels are expected to uphold high standards of ethical and moral behavior. The following code of conduct is outlined for them.
                            </p>

                            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4 border-t border-white/15 pt-7">

                                {[
                                    "Respect",
                                    "Safety",
                                    "Discipline",
                                    "Community Living",
                                ].map((item) => (
                                    <div key={item} className="flex items-center gap-3">
                                        <span className="h-1.5 w-1.5 rotate-45 bg-[#D5AD57]" />
                                        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/65">{item}</span>
                                    </div>
                                ))}

                            </div>

                        </div>


                        {/* RIGHT */}
                        <div className="relative hidden border-l border-white/10 lg:flex lg:items-center">

                            <span className="absolute right-[-20px] top-[-30px] font-['Libre_Caslon_Display',Georgia,serif] text-[270px] font-normal leading-none tracking-[-0.08em] text-white/[0.03]">
                                08
                            </span>

                            <div className="relative w-full px-10 xl:px-14">

                                <div className="flex items-center justify-between">
                                    <div className="grid h-16 w-16 place-items-center rounded-full border border-[#D5AD57]/40 text-[#D5AD57]">
                                        <ShieldIcon />
                                    </div>

                                    <div className="text-right">
                                        <strong className="block font-['Libre_Caslon_Display',Georgia,serif] text-[58px] font-normal leading-none text-[#D5AD57]">08</strong>
                                        <span className="mt-2 block text-[9px] font-bold uppercase tracking-[0.16em] text-white/40">Residential Standards</span>
                                    </div>
                                </div>

                                <div className="mt-10">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D5AD57]">Residential Life Framework</span>

                                    <h2 className="mt-4 max-w-[440px] font-['Libre_Caslon_Display',Georgia,serif] text-[36px] font-normal leading-[1.15]">
                                        A secure, respectful and enriching place to live.
                                    </h2>
                                </div>

                                <div className="mt-9 grid grid-cols-2 border-l border-t border-white/10">

                                    {[
                                        ["01", "Respect"],
                                        ["02", "Quiet Hours"],
                                        ["03", "Cleanliness"],
                                        ["04", "Safety"],
                                        ["05", "Substances"],
                                        ["06", "Smoking"],
                                        ["07", "Policies"],
                                        ["08", "Discipline"],
                                    ].map(([number, title]) => (
                                        <div key={number} className="flex items-center gap-3 border-b border-r border-white/10 px-4 py-4">
                                            <span className="font-['Libre_Caslon_Display',Georgia,serif] text-[17px] text-[#D5AD57]">{number}</span>
                                            <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-white/55">{title}</span>
                                        </div>
                                    ))}

                                </div>

                            </div>

                        </div>

                    </div>

                </section>

                {/* HOSTEL CODE */}
                <section className="relative overflow-visible bg-[#F7F2E8] py-20 sm:py-24 lg:py-28">

                    <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">

                        {/* INTRO */}
                        <div className="grid gap-10 border-b border-[#071A2F]/15 pb-12 lg:grid-cols-[1fr_440px] lg:items-end">

                            <div>
                                <Eyebrow>Residential Community Standards</Eyebrow>

                                <Title before="A Better Campus Life" highlight="Starts With Responsibility" after="" position="middle" theme="burgundy" />
                            </div>

                            <p className="text-[15px] leading-8 text-[#65727E]">
                                The boarders of the hostels are expected to uphold high standards of ethical and moral behavior. The following code of conduct is outlined for them.
                            </p>

                        </div>


                        {/* SIDEBAR + CONTENT */}
                        <div className="mt-14 grid items-start gap-12 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-16">

                            {/* FULL HEIGHT WRAPPER REQUIRED FOR STICKY */}
                            <div className="relative hidden h-full lg:block">

                                <aside className="sticky top-[145px]">

                                    <div className="border border-[#071A2F]/10 bg-white">

                                        <div className="border-b border-[#071A2F]/10 p-6">
                                            <div className="flex items-center justify-between">

                                                <span className="text-[10px] font-bold uppercase tracking-[0.17em] text-[#8B1E3F]">
                                                    Hostel Standards
                                                </span>

                                                <span className="font-['Libre_Caslon_Display',Georgia,serif] text-[23px] text-[#D5AD57]">
                                                    08
                                                </span>

                                            </div>

                                            <p className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-[24px] leading-[1.2] text-[#071A2F]">
                                                General Conduct
                                            </p>
                                        </div>

                                        <nav className="px-6">

                                            {hostelSections.map((item) => (
                                                <a key={item.id} href={`#${item.id}`} className="group grid grid-cols-[34px_1fr] gap-3 border-b border-[#071A2F]/10 py-3.5 last:border-b-0">
                                                    <span className="font-['Libre_Caslon_Display',Georgia,serif] text-[17px] text-[#D5AD57]">{item.number}</span>
                                                    <span className="text-[12px] font-semibold leading-5 text-[#071A2F]/60 transition group-hover:text-[#8B1E3F]">{item.title}</span>
                                                </a>
                                            ))}

                                        </nav>

                                    </div>

                                    <div className="mt-5 bg-[#8B1E3F] p-6 text-white">
                                        <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#D5AD57]">Residential Principle</span>

                                        <p className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-[24px] leading-[1.3]">
                                            Respect the space. Protect the community.
                                        </p>
                                    </div>

                                </aside>

                            </div>


                            {/* CONTENT */}
                            <div className="min-w-0">

                                {/* GENERAL CONDUCT TITLE */}
                                <div className="mb-3 flex items-center justify-between border-b border-[#071A2F]/15 pb-6">

                                    <div>
                                        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">Hostel Board</span>

                                        <h2 className="mt-2 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(36px,4vw,52px)] font-normal leading-none text-[#071A2F]">
                                            General Conduct
                                        </h2>
                                    </div>

                                    <span className="hidden font-['Libre_Caslon_Display',Georgia,serif] text-[70px] leading-none text-[#D5AD57]/35 sm:block">
                                        01—08
                                    </span>

                                </div>


                                {/* POLICY TIMELINE */}
                                <div className="relative">

                                    <div className="absolute bottom-0 left-[25px] top-0 hidden w-px bg-[#071A2F]/10 sm:block" />

                                    {hostelSections.map((section, index) => (
                                        <article key={section.id} id={section.id} className="scroll-mt-[140px] relative border-b border-[#071A2F]/15 py-10 first:pt-8">

                                            <div className="grid gap-7 sm:grid-cols-[52px_210px_1fr] lg:grid-cols-[52px_230px_1fr]">

                                                {/* NUMBER */}
                                                <div className="relative z-10">
                                                    <span className={`grid h-[52px] w-[52px] place-items-center rounded-full border font-['Libre_Caslon_Display',Georgia,serif] text-[18px] ${index === 3 || index === 7 ? "border-[#8B1E3F] bg-[#8B1E3F] text-white" : "border-[#D5AD57] bg-[#F7F2E8] text-[#A77B2D]"}`}>
                                                        {section.number}
                                                    </span>
                                                </div>


                                                {/* TITLE */}
                                                <div>
                                                    <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">{section.label}</span>

                                                    <h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-[27px] font-normal leading-[1.12] text-[#071A2F]">
                                                        {section.title}
                                                    </h3>
                                                </div>


                                                {/* DETAILS */}
                                                <div>

                                                    <p className="mb-5 text-[14px] leading-7 text-[#65727E]">
                                                        {section.description}
                                                    </p>

                                                    <ul className="border-t border-[#071A2F]/10">

                                                        {section.points.map((point, pointIndex) => (
                                                            <li key={pointIndex} className="grid grid-cols-[24px_1fr] gap-4 border-b border-[#071A2F]/10 py-4">

                                                                <span className="mt-[9px] h-1.5 w-1.5 rotate-45 bg-[#D5AD57]" />

                                                                <span className="text-[14px] leading-7 text-[#52606C]">
                                                                    {point}
                                                                </span>

                                                            </li>
                                                        ))}

                                                    </ul>

                                                </div>

                                            </div>

                                        </article>
                                    ))}

                                </div>

                            </div>

                        </div>

                    </div>

                </section>


                {/* CLOSING BAND */}
                <section className="bg-[#071A2F] text-white">

                    <div className="mx-auto grid w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)] lg:grid-cols-[.7fr_1.3fr]">

                        <div className="border-b border-white/10 py-16 lg:border-b-0 lg:border-r lg:py-20 lg:pr-14">
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D5AD57]">Hostel Community</span>

                            <h2 className="mt-5 font-['Libre_Caslon_Display',Georgia,serif] text-[38px] font-normal leading-[1.1]">
                                Safe living is a shared responsibility.
                            </h2>
                        </div>

                        <div className="flex items-center py-16 lg:py-20 lg:pl-16">
                            <p className="max-w-[760px] text-[16px] leading-8 text-white/60">
                                Every resident contributes to creating a hostel environment that supports safety, privacy, academic focus, mutual respect, and the wellbeing of the University community.
                            </p>
                        </div>

                    </div>

                </section>

            </main>

            <Footer />
        </>
    );
}